import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import {
  listNominations, nominationAnalytics,
  type Nomination, type NominationAnalytics,
} from './api'
import { channelLabel, seatLabel } from './vocab'

/**
 * Turnout, the curve, channel mix, and what is worth a second look.
 *
 * ## Turnout of WHAT
 *
 * Of the frozen roll — the members eligible for this contest. Not the party's membership and
 * not the voter register. The three denominators differ by orders of magnitude, so the
 * eligible count is shown beside every percentage rather than left to be assumed.
 *
 * ## The curve is the point
 *
 * A total tells you how many voted. The shape tells you whether it looks like people
 * arriving through a day or like a script running for four minutes, and only one of those is
 * visible in a number.
 */
export function AnalyticsPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const nominationId = params.get('nomination')

  const [contests, setContests] = useState<Nomination[]>([])
  const [data, setData] = useState<NominationAnalytics | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!electionId) return
    void (async () => {
      try {
        setContests(await listNominations({ electionId, size: 300 }))
      } catch (err) {
        setError(explain(err))
      }
    })()
  }, [electionId])

  const load = useCallback(async () => {
    if (!nominationId) { setData(null); return }
    try {
      setError(null)
      setData(await nominationAnalytics(nominationId))
    } catch (err) {
      setError(explain(err))
      setData(null)
    }
  }, [nominationId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="Nomination analytics" />

  const peak = Math.max(1, ...(data?.curve.map((c) => c.votes) ?? [1]))

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-erp-base font-semibold">Analytics</h2>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Turnout of the frozen roll, how it arrived, and where it came from.
        </p>
      </div>

      <label className="block max-w-xl">
        <span className="block text-erp-xs text-muted-foreground mb-1">Contest</span>
        <select value={nominationId ?? ''} className="erp-input h-9 w-full text-erp-sm"
          onChange={(e) => setParams((p) => {
            if (e.target.value) p.set('nomination', e.target.value)
            else p.delete('nomination')
            return p
          }, { replace: true })}>
          <option value="">Choose a contest…</option>
          {contests.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} · {seatLabel(c.seatTypeCode)}
            </option>
          ))}
        </select>
      </label>

      <ErrorStrip message={error} className="rounded-md border" />

      {!nominationId && (
        <EmptyState title="Choose a contest" hint="Analytics are per contest." />
      )}

      {nominationId && data === null && !error && <LoadingBlock />}

      {data && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="On the roll" value={fmtNumber(data.summary.eligible)} />
            <MetricCard label="Voted" value={fmtNumber(data.summary.voted)} />
            <MetricCard label="Turnout"
              value={data.summary.turnoutPct == null ? '—' : `${data.summary.turnoutPct}%`} />
            <MetricCard label="Assisted"
              value={data.summary.assistedPct == null ? '—'
                : `${data.summary.assistedPct}%`} />
          </div>

          <Note tone="info">
            Turnout is of the <strong>frozen roll</strong> ({fmtNumber(data.summary.eligible)}
            {' '}members eligible for this contest) — not of the party's membership and not of
            the voter register.
          </Note>

          {/* ── flags ─────────────────────────────────────────────────── */}
          {data.flags.length > 0 && (
            <div className="space-y-1.5">
              <h3 className="text-erp-sm font-medium">Worth a look</h3>
              {data.flags.map((f, i) => (
                <div key={`${f.code}-${i}`}
                  className={cn('flex items-start gap-2 rounded-md border px-3 py-2 text-erp-sm',
                    f.severity === 'WARN'
                      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
                      : 'border-border bg-muted/40 text-muted-foreground')}>
                  {f.severity === 'WARN'
                    ? <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    : <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
                  <span>
                    <strong>{f.where}</strong> — {f.detail}
                  </span>
                </div>
              ))}
              <p className="text-erp-xs text-muted-foreground">
                These are not accusations. Every one has an innocent reading — a small ward
                really can turn out 97% — they are just the short list worth a phone call.
              </p>
            </div>
          )}

          {/* ── the curve ─────────────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">When the votes arrived</h3>
            {data.curve.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">Nothing cast yet.</p>
            ) : (
              <div className="axis-table-scroll">
                <div className="flex items-end gap-0.5 h-24 min-w-full">
                  {data.curve.map((c) => (
                    <div key={c.bucket} className="flex-1 min-w-[3px] group relative"
                      title={`${fmtDateTime(c.bucket)} — ${c.votes} vote(s)`}>
                      <div className="bg-chocolate rounded-t transition-[height]"
                        style={{ height: `${Math.max(2, (c.votes / peak) * 96)}px` }} />
                    </div>
                  ))}
                </div>
                <p className="text-erp-xs text-muted-foreground mt-1">
                  15-minute buckets. A steady shape is what a real primary looks like; a
                  single spike is worth explaining.
                </p>
              </div>
            )}
          </div>

          {/* ── channels ──────────────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">How they voted</h3>
            <div className="flex flex-wrap gap-3">
              {data.channels.map((c) => (
                <div key={c.channelCode} className="rounded-md border border-border px-3 py-2">
                  <span className="block text-erp-xs text-muted-foreground">
                    {channelLabel(c.channelCode)}
                  </span>
                  <span className="block text-erp-base font-semibold tabular-nums">
                    {fmtNumber(c.votes)}
                    {c.sharePct != null && (
                      <span className="ml-1.5 text-erp-xs font-normal text-muted-foreground">
                        {c.sharePct}%
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── by place ──────────────────────────────────────────────── */}
          <div>
            <h3 className="text-erp-sm font-medium mb-1.5">By place</h3>
            <ErpTable head={(
              <>
                <Th>Place</Th>
                <Th className="text-right">On the roll</Th>
                <Th className="text-right">Voted</Th>
                <Th className="text-right">Turnout</Th>
                <Th className="text-right">Assisted</Th>
              </>
            )}>
              {data.byPlace.length === 0 ? (
                <tr><Td className="text-muted-foreground">Nothing counted yet.</Td></tr>
              ) : data.byPlace.map((p) => (
                <tr key={p.unitPath}>
                  <Td>
                    {p.unitName}
                    {p.unitTypeCode && (
                      <span className="ml-1.5 text-erp-xs text-muted-foreground">
                        {p.unitTypeCode.replace('_', ' ').toLowerCase()}
                      </span>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums">{fmtNumber(p.eligible)}</Td>
                  <Td className="text-right tabular-nums font-medium">{fmtNumber(p.voted)}</Td>
                  <Td className={cn('text-right tabular-nums',
                    p.turnoutPct != null && p.turnoutPct > 95
                      && 'text-amber-700 dark:text-amber-400 font-medium')}>
                    {p.turnoutPct == null ? '—' : `${p.turnoutPct}%`}
                  </Td>
                  <Td className="text-right tabular-nums">{fmtNumber(p.assisted)}</Td>
                </tr>
              ))}
            </ErpTable>
          </div>
        </>
      )}
    </div>
  )
}
